import { useCallback, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, LinearProgress } from '@mui/material';
// Components
import { PageTemplate } from 'src/shared/templates/pageTemplate';
import ImageDropZone from 'src/shared/components/form/imageDropZone';
import ControlledTextInput from 'src/shared/components/form/controlledTextInput';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import ControlledEditorInput from 'src/shared/components/form/controlledEditorInput';
import ControlledCanHaveChildren from 'src/shared/components/form/controlledCanHaveChildren';
import { BoxSaveCancelButtons } from 'src/shared/components/organisms/boxSaveCancelButtons';
import ControlledPushNoticeCheckbox from 'src/shared/components/form/controlledPushNoticeCheckbox';
import SegmentLocationRoleTags from 'src/shared/components/form/segmentLocationRoleTags';
import { extraMenu } from 'src/shared/components/navigations/menuList';
import LabelWithTooltip from 'src/shared/components/organisms/labelWithToolTip';
// Services
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { IParams } from 'src/shared/interfaces/params';
import { ContentPageSchema } from 'src/shared/schemas/contentPageSchema';
import { IContentPage } from 'src/shared/interfaces/contentPageInterface';
import { ContentTypeEnum } from 'src/shared/enums/contentType.enum';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { getMenuListAction } from 'src/shared/services/redux/actions/getMenuList';
import ControlledSwitchNorway from 'src/shared/components/form/controlledSwitchNorway';

const CreateMenuPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<IParams>();
  const dispatch = useDispatch();

  // Global states
  const { userCompany } = useSelector(
    (state: RootStateType) => state.authState,
  );

  // Local states
  const [selectedImage, setSelectedImage] = useState<File>();
  const [loading, setLoading] = useState<boolean>(false);
  const [parentContentPage, setParentContentPage] =
    useState<ContentPageResponse>();

  const formMethods = useForm<IContentPage>({
    resolver: yupResolver(ContentPageSchema(t)),
    defaultValues: {
      ID: 1,
      title: '',
      content: '',
      featuredImageText: '',
      sortIndex: 0,
      companyId: parentContentPage?.companyId ?? 1, // TODO: Userns companyId?
      companyId2: userCompany?.id === 1 ? 32 : null,
      parentContentPageId: parentContentPage?.id ?? Number(id),
      contentType: ContentTypeEnum.MenuPage,
      businessAreaTags: [],
      locations: [],
      canHaveChildren: false,
      sendNotice: false,
      sendEmail: false,
      publishDate: dayjs().format('YYYY-MM-DD'),
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = formMethods;

  const getParentContentPageDetails = useCallback(async () => {
    try {
      const newsAPI = new ContentPageAPI();
      setLoading(true);
      const res = await newsAPI.Get(id);
      setParentContentPage(res?.result);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      ConsoleHelper.log(error ?? 'Unkown error: getparentContentPageDetails');
    }
  }, [id]);

  useEffect(() => {
    getParentContentPageDetails();
  }, [getParentContentPageDetails, id]);

  const getLinkToParent = () => {
    let linkTo = '/';
    if (parentContentPage) {
      linkTo = `/${parentContentPage?.contentType}/${id}`;
    }
    return linkTo;
  };

  const formSubmit: SubmitHandler<IContentPage> = async (
    data: IContentPage,
  ) => {
    const newsData = data;
    setLoading(true);
    const formData = new FormData();
    // Om featured image vald
    if (selectedImage) {
      formData.append('featuredDocument', selectedImage);
    }
    const businessAreaTagIDs: any[] = [];
    if (data.businessAreaTags) {
      data.businessAreaTags.forEach((tag: any) => {
        if (tag.id) {
          businessAreaTagIDs.push(tag.id.toString());
        }
      });
    }
    const tagIDs: string[] = [];
    if (data?.locations) {
      data.locations.forEach((element) => {
        if (element.id) {
          tagIDs.push(element.id.toString());
        }
      });
    }
    if (data?.roles) {
      data.roles.forEach((element) => {
        if (element.id) {
          tagIDs.push(element.id.toString());
        }
      });
    }
    tagIDs.forEach((element) => {
      formData.append('tags', element);
    });
    businessAreaTagIDs.forEach((element) => {
      formData.append('businessAreaTags', element);
    });
    formData.append('title', newsData.title);
    formData.append('iD', newsData?.ID?.toString() ?? '1');
    formData.append('content', newsData.content ?? '');
    formData.append('featuredImageText', newsData.featuredImageText ?? '');
    if (userCompany?.id) {
      formData.append('companyId', userCompany?.id?.toString());
    }
    // Publicera till norska bolag från Instalco SE
    if (data.companyId2 === 32) {
      formData.append('companyId2', '32');
    }
    formData.append('hideInMenu', 'false');
    formData.append('sendNotice', data.sendNotice.toString());
    formData.append('sendEmail', data.sendEmail.toString());
    formData.append('canHaveChildren', data.canHaveChildren ? 'true' : 'false');
    formData.append('contentType', newsData.contentType ?? '');
    formData.append('sortIndex', newsData.sortIndex ?? '0');
    if (parentContentPage && parentContentPage.id !== 0) {
      formData.append('parentContentPageId', parentContentPage?.id?.toString());
    }
    formData.append(
      'publishDate',
      dayjs(newsData.publishDate).format('YYYY-MM-DD'),
    );
    try {
      const newsAPI = new ContentPageAPI();
      await newsAPI.CreateFormData(formData);
      dispatch(getMenuListAction(extraMenu(t), userCompany?.id));
      setLoading(false);
      toast.success(`${t('Done!')}`, {
        theme: 'colored',
      });
      reset();
      history.push(getLinkToParent());
    } catch (error) {
      ConsoleHelper.log('error create menupage::', error);
      setLoading(false);
      toast.error(t('SomethingWentWrong'));
    }
  };

  // Ändring av featured image
  const onFeaturedImageChange = useCallback((file: File | undefined) => {
    if (file) {
      ConsoleHelper.log('file for featuredImage', file);
      setSelectedImage(file);
    } else setSelectedImage(undefined);
  }, []);

  return (
    <PageTemplate
      header={t('createMenuPage')}
      headerContent=''
      content={
        <Box sx={{ p: 1 }}>
          {loading && <LinearProgress />}
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(formSubmit)}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <ControlledTextInput
                  name='title'
                  label={t('Title')}
                  type='text'
                />
                <LabelWithTooltip
                  label={t('FeaturedImage')}
                  infoContent={t('TooltipFeaturedImage')}
                />
                <ImageDropZone
                  onFilesChange={onFeaturedImageChange}
                  initialFile={undefined}
                />
                <ControlledTextInput
                  name='featuredImageText'
                  label={t('imageText')}
                  type='text'
                />
                <ControlledEditorInput
                  name='content'
                  label={t('Description')}
                />
                <LabelWithTooltip
                  label={t('PushContentTo')}
                  infoContent={t('TooltipPushContentTo')}
                />
                <SegmentLocationRoleTags
                  companyId={parentContentPage?.companyId ?? 0}
                  control={control}
                  errors={errors}
                />
                <ControlledPushNoticeCheckbox control={control} />
                <ControlledTextInput
                  name='sortIndex'
                  label={t('MenuSortingOrder')}
                  type='text'
                />
                <ControlledCanHaveChildren control={control} />
                <ControlledDatePicker
                  label={t('PublishDate')}
                  name='publishDate'
                />
                <ControlledSwitchNorway name='companyId2' />
                <BoxSaveCancelButtons
                  onCancel={() => reset()}
                  linkTo={getLinkToParent()}
                  saveDisabled={loading}
                />
              </Box>
            </form>
          </FormProvider>
        </Box>
      }
    />
  );
};

export default CreateMenuPage;
